import { formatMoney, formatNumber } from '@ecom/sdk';
import type { AdminLocale } from '@/i18n';

/**
 * Display formatting.
 *
 * Money arrives as integer minor units and is divided exactly once, here
 * (ADR-004). Nothing in the admin does arithmetic on a money value.
 *
 * Digits follow the language being read — Arabic-Indic in Arabic, as the
 * reference dashboards and regional invoices use — **except** for anything a
 * person types, copies, or dictates: SKUs, tracking numbers, order numbers, and
 * settings values stay Latin, where digit shape is identity rather than style.
 */

const DEFAULT_CURRENCY = 'SAR';

export function money(amountMinor: number, locale: AdminLocale, currency = DEFAULT_CURRENCY): string {
  return formatMoney(amountMinor, currency, locale, { hideDecimalsWhenWhole: true });
}

/** A total that may not exist: an average over zero orders is "—", not zero. */
export function moneyOrDash(
  amountMinor: number | null | undefined,
  locale: AdminLocale,
  currency = DEFAULT_CURRENCY,
): string {
  if (amountMinor === null || amountMinor === undefined) return '—';
  return money(amountMinor, locale, currency);
}

export function count(value: number, locale: AdminLocale): string {
  return formatNumber(value, locale);
}

/** A file's weight, as a person reads it: "482 KB", "1.2 MB". */
export function fileSize(bytes: number, locale: AdminLocale): string {
  if (bytes >= 1024 * 1024) return `${formatNumber(Math.round((bytes / (1024 * 1024)) * 10) / 10, locale)} MB`;
  return `${formatNumber(Math.max(1, Math.round(bytes / 1024)), locale)} KB`;
}

/** Identifiers keep Latin digits in both languages — they are read aloud. */
export function identifier(value: string): string {
  return value;
}

export function date(value: string | null | undefined, locale: AdminLocale): string {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    dateStyle: 'medium',
  }).format(parsed);
}

/** Today as a dashboard heads it: "Friday 9 October", in the reader's language and digits. */
export function longDay(now: Date, locale: AdminLocale): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);
}

/** Which greeting the hour calls for: before noon, before six, or after. */
export function partOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * A promised delivery window (R6): `YYYY-MM-DD` days in the store's calendar,
 * formatted in UTC so no browser's own zone can move a day.
 */
export function dayRange(from: string, to: string, locale: AdminLocale): string {
  const format = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  return from === to ? format.format(start) : format.formatRange(start, end);
}

export function dateTime(value: string | null | undefined, locale: AdminLocale): string {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "3h ago" — the action queue is ordered by age, so age is what it shows.
 * Deliberately coarse: an operator needs "is this rotting?", not seconds.
 */
export function age(value: string | null | undefined, locale: AdminLocale, now = Date.now()): string {
  if (!value) return '—';

  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return '—';

  const elapsed = Math.max(0, now - parsed);
  const relative = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar-SA' : 'en', { numeric: 'auto' });

  if (elapsed < HOUR) return relative.format(-Math.max(1, Math.floor(elapsed / MINUTE)), 'minute');
  if (elapsed < DAY) return relative.format(-Math.floor(elapsed / HOUR), 'hour');

  return relative.format(-Math.floor(elapsed / DAY), 'day');
}

/**
 * A chart axis's money: "12K", "١٫٢ ألف" — riyals, compact, no symbol (the
 * chart's title says what it is). Divided once, here, for display only.
 */
export function compactMoney(amountMinor: number, locale: AdminLocale): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amountMinor / 100);
}

/** A `YYYY-MM-DD` day, short: "3 Sep" — in UTC, so no browser's zone moves it. */
export function shortDay(day: string, locale: AdminLocale): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

/** A signed change in whole percent: "+12%", "−4%", "+١٢٪". */
export function signedPercent(percent: number, locale: AdminLocale): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: 0,
  }).format(percent / 100);
}

/** A share of a whole in whole percent: "38%", "٣٨٪". */
export function share(part: number, whole: number, locale: AdminLocale): string {
  if (whole <= 0) return '—';
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', { style: 'percent', maximumFractionDigits: 0 }).format(part / whole);
}

/** Title Case from an enum: `AWAITING_COLLECTION` → `Awaiting collection`. */
export function humanize(value: string): string {
  const words = value.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
